import { EmberInvasionCandidate } from "game-common/models"

// Presentation helpers for the ember end-of-run scorecard (see run_summary.ts). Split out so they
// can be unit-tested without pulling in the panel's pixi/paperdoll dependencies.
//
// Non-negotiable: run_summary.ts itself CANNOT be instantiated under jest -- importing it pulls in
// the pixi sprite graph, which dies on a circular import (`SignSprite extends undefined`). So every
// RULE the panel enforces lives here and the widget only sizes and places what these functions say.

// How a creature kind from the server is presented. Keys are what ember-controller.js reports: a
// wave enemy's orchestrator role, a wilderness enemy's race, or "rabbit". Anything unknown still
// renders (title-cased, with the generic skull) so a new creature type needs no client change.
const CREATURE_META: Record<string, { label: string; emoji: string }> = {
    skirmisher: { label: "Skirmishers", emoji: "💀" },
    raider: { label: "Raiders", emoji: "🗡️" },
    archer: { label: "Archers", emoji: "🏹" },
    bruiser: { label: "Bruisers", emoji: "🧟" },
    eliteRaider: { label: "Elite raiders", emoji: "👹" },
    slime: { label: "Slimes", emoji: "🟢" },
    snake: { label: "Snakes", emoji: "🐍" },
    redspider: { label: "Spiders", emoji: "🕷️" },
    rabbit: { label: "Rabbits", emoji: "🐇" },
}

export const creatureMeta = (key: string): { label: string; emoji: string } =>
    CREATURE_META[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), emoji: "💀" }

// m:ss, or h:mm:ss for the rare long run.
export const formatDuration = (ms: number): string => {
    const total = Math.max(0, Math.round(ms / 1000))
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

// What the panel needs to know about the camp's lives, in one place: how many embers to draw, how
// many of them are burnt out, what to write underneath, and — the load-bearing part — whether this
// camp will have the player back at all.
//
// A life is spent by DYING, so `spent` already counts the death that put this panel up. A life is
// what buys "Try again" (get up in this camp and carry the run on), so once they are all spent the
// camp is finished with the player and only "Restart" — a fresh camp, always available — is left.
// The server enforces the same rule; this is what the panel DRAWS, not what decides it.
export interface LivesView {
    total: number
    spent: number
    remaining: number
    canTryAgain: boolean
    caption: string
}

export const livesView = (summary: { livesTotal?: number; livesSpent?: number }): LivesView => {
    // A summary from a camp that doesn't report lives at all (an older server) reads as "no lives
    // to show" rather than as "out of lives": the panel then hides the embers and leaves both ways
    // on available, which is how it behaved before lives existed.
    const total = Math.max(0, Math.floor(summary?.livesTotal || 0))
    const spent = Math.max(0, Math.min(total, Math.floor(summary?.livesSpent || 0)))
    const remaining = total - spent
    return {
        total,
        spent,
        remaining,
        canTryAgain: total === 0 || remaining > 0,
        caption: total === 0 ? "" : remaining > 0 ? `${spent} OF ${total} SPENT` : "ALL SPENT",
    }
}

// The ways on the panel offers, in the order they are drawn. Which buttons EXIST is the rule this
// panel is really enforcing, so it lives here, in the half that can be tested without pixi: the
// widget only decides how wide they are and where they sit.
//
// While the camp has a life left there are two, with "try again" lit as the primary one. Once it
// hasn't, there is exactly ONE -- the spent option is not drawn at all rather than drawn dead,
// because a greyed-out button teaches nothing the burnt-out embers above it haven't already said
// and leaves the player hunting for which half of the panel still works.
//
// ...plus, on any death, a THIRD way on: rising in somebody else's camp as one of its attackers.
// With lives still in hand that is a forfeit, and says so. See below, and
// docs/ember-the-fallen-design.md ("Offer").
export type RunSummaryAction = "tryAgain" | "restart" | "invade"

export interface RunSummaryButton {
    action: RunSummaryAction
    label: string
    sublabel: string
    primary: boolean
    // Always true now that the invade button is absent rather than inert when there is nowhere to
    // go. Kept because the widget's drawing still reads it, and a future state may want it again.
    enabled: boolean
    // Drawn small, on a row of its own under the ordinary ways on, rather than sharing their row.
    // This is the panel's HIERARCHY, and it is the dial that stops the camp game becoming
    // instrumental: the instant rising reads as the best answer to dying, the camp game has become
    // the thing you get through.
    minor: boolean
}

const RESTART_BUTTON = { action: "restart" as RunSummaryAction, label: "RESTART", sublabel: "A FRESH CAMP" }

// What the picker is for, in the two words the player has to recognise later.
const INVADE_LABEL = "RESURRECT AS UNDEAD!"
export const INVADE_READY_SUBLABEL = "RISE IN A STRANGER'S CAMP"

/**
 * What the button says while the camp STILL HAS LIVES, where going costs the rest of them.
 *
 * Every exit from a visit builds the visitor a fresh camp, so leaving now abandons a run that could
 * still have been held -- and that forfeit is the whole reason this button was once withheld until
 * the run was over. Withholding it made the feature almost unreachable (it wanted a camp open AND
 * somebody else thriving AND the lives gone, three coincidences deep), so the door is open and the
 * cost is stated on it instead. The honest answer to a trap is to make it a decision.
 */
export const invadeForfeitSublabel = (remaining: number): string =>
    remaining === 1 ? "ENDS YOUR RUN — 1 LIFE LEFT" : `ENDS YOUR RUN — ${remaining} LIVES LEFT`

// The ways on, given the camp's lives and whatever the invasion feed last said.
//
// `candidates` is NOT a reply to a press: Protocol.type_updateInvasionOffer is an interval feed
// (~2s), so this is simply the newest thing it said, and the panel re-runs this rule every frame it
// is up -- which is what makes the third button light up BY ITSELF when a camp becomes available.
// Its three states are all different answers, and the last two are the ones easy to collapse:
//
//   undefined -- the feed has never spoken: an older server, or a room with no invasion campaign.
//   []        -- it HAS spoken, and nothing is open. On a quiet server that is the normal steady
//                state, and it used to be said out loud on a drawn-but-inert button. That was wrong
//                in play: a permanently dim button on every scorecard is furniture, and the player
//                learns to stop looking at it.
//   [...]     -- go.
//
// So the first two are the SAME answer -- no button -- and the button's ARRIVAL is the signal. That
// is worth more than any colour change could be: something appearing on a panel that has been
// sitting still is the most attention-getting thing a static screen can do, and it is only honest
// because the feed is live (see Protocol.type_updateInvasionOffer).
//
// Offered on ANY death, not only once the run is over. It was gated on "the run is over" to start
// with, and that was wrong in the first playtest: a player who dies at wave 3 with two lives left is
// the commonest death there is, and the gate meant the button needed a camp open AND somebody else
// thriving AND the lives gone before it would ever appear. The forfeit the gate was protecting
// against is real -- every exit from a visit builds a FRESH camp, so going now abandons a run that
// still had lives in it -- so it is written on the button (see invadeForfeitSublabel) rather than
// used as a reason to hide the door.
//
// What stops rising becoming the best answer to dying is therefore the panel's HIERARCHY alone:
// third in reading order, drawn minor, on its own row under the ordinary ways on, and never primary.
// That is the dial to turn if this ever reads as the point of the camp game.
export const runSummaryButtons = (
    summary: { livesTotal?: number; livesSpent?: number },
    candidates?: EmberInvasionCandidate[] | null,
): RunSummaryButton[] => {
    const lives = livesView(summary)
    const buttons: RunSummaryButton[] = lives.canTryAgain
        ? [
              {
                  action: "tryAgain",
                  label: "TRY AGAIN",
                  sublabel: "HOLD THIS CAMP",
                  primary: true,
                  enabled: true,
                  minor: false,
              },
              { ...RESTART_BUTTON, primary: false, enabled: true, minor: false },
          ]
        : [{ ...RESTART_BUTTON, primary: true, enabled: true, minor: false }]

    // Only ever drawn when there is somewhere to GO. See the note above the three states.
    if (candidates && candidates.length > 0) {
        buttons.push({
            action: "invade",
            label: INVADE_LABEL,
            // Rise and lose nothing (the run was over anyway), or rise and spend what is left of
            // this camp -- which the button says out loud, because every exit builds a fresh one.
            sublabel: lives.canTryAgain
                ? invadeForfeitSublabel(Math.max(1, lives.remaining))
                : INVADE_READY_SUBLABEL,
            primary: false,
            enabled: true,
            minor: true,
        })
    }
    return buttons
}

// ===== the invade button's arrival =================================================
// Timing, not drawing -- so it lives here with the reveal's beats, in the half that tests without
// pixi. The widget (run_summary.ts) only draws what these numbers say.
//
// The flare is what makes the button's APPEARING the signal rather than its colour: it is the only
// button that turns up while the panel is already open, so for a moment it is oversized and
// white-hot and then settles. Deliberately brief -- long enough to catch an eye that was reading the
// kill tally, short enough not to become the panel.
export const INVADE_FLARE_MS = 520
// ...and then it breathes, so it keeps the eye after the flare has gone. An ember, which is the one
// visual language this panel already speaks.
export const INVADE_BREATH_MS = 1900

/** The flare's strength: 1 at the instant of arrival, 0 once it has settled. */
export const invadeFlare = (elapsedMs: number): number => {
    const t = Math.min(1, Math.max(0, elapsedMs / INVADE_FLARE_MS))
    // Eased out, so it slams in and drains away rather than fading evenly.
    return (1 - t) * (1 - t)
}

// ===== the opening flourish ===========================================================
// The scorecard does not open on its numbers. It opens on the ONE number a run is measured by --
// how far into the night the camp got -- thrown up in the biggest type the screen has, held long
// enough to land, and then handed DOWN into the panel: the headline lifts away while the panel
// rises under it, the two overlapping, so the flourish becomes the scorecard rather than ending
// and being replaced by it.
//
// The number is held for a full three seconds before it starts to leave -- long enough to be read,
// sat with, and resented. The handoff then runs under half a second on top of that, overlapping
// the number's exit, so the beat as a whole is a shade over three seconds. The timing lives here,
// in the half that tests without pixi; the widget (run_summary_reveal.ts) only draws what these
// numbers say.

// The beats, in milliseconds from the panel opening.
export const REVEAL_STRIKE_MS = 260 // the number slams in, throwing a shockwave
export const REVEAL_HOLD_END_MS = 3000 // ...and is held, breathing, while embers rise past it
export const REVEAL_HANDOFF_MS = 480 // ...then lifts away as the panel rises into its place
export const REVEAL_TOTAL_MS = REVEAL_HOLD_END_MS + REVEAL_HANDOFF_MS

// How far into the handoff the panel starts coming up. Well before the headline is gone: the
// overlap is what makes it read as one movement instead of two.
const PANEL_ENTRY_AT = 0.25

// The slam: the headline arrives oversized and snaps down past its resting size before settling.
const SLAM_FROM = 2.05
// The hold's breath, and how fast it breathes.
const BREATH = 0.014
// One unhurried cycle across the hold, so a three-second pause reads as something alive rather
// than as a frozen frame.
const BREATH_HZ = (2 * Math.PI) / 3000
// What the headline does on its way out.
const EXIT_SHRINK = 0.22
const EXIT_LIFT = 34
// The panel's rise, in stage units.
const PANEL_RISE = 22

const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t)
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - clamp01(t), 3)
const easeInOutCubic = (t: number): number => {
    const x = clamp01(t)
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}
// Overshoots its target and comes back, which is what makes the slam land rather than glide.
const easeOutBack = (t: number): number => {
    const x = clamp01(t)
    const c1 = 1.9
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2)
}
// A band of a timeline, as 0..1. Used for the staggered fades (kicker, then epithet).
const band = (elapsedMs: number, fromMs: number, toMs: number): number =>
    clamp01((elapsedMs - fromMs) / (toMs - fromMs))

export interface WaveRevealFrame {
    // The flourish is over: the widget comes down and the panel is left standing on its own.
    done: boolean
    // The headline block (kicker + number + epithet), which moves as one.
    alpha: number
    scale: number
    lift: number // stage units it has risen off its resting line
    // The two smaller lines come in after the number, not with it.
    kickerAlpha: number
    epithetAlpha: number
    // The shockwave the strike throws off: 0..1 while it expands, -1 once it is spent.
    ringT: number
    // Whether embers are still being thrown up past the headline. They stop being emitted as it
    // starts to leave, so the last of them are still in the air over the arriving panel.
    emitting: boolean
    // What the panel underneath is doing. Zero alpha means "not up yet" -- the widget hides it
    // outright rather than leaving invisible buttons to be pressed.
    panelAlpha: number
    panelRise: number
}

// The whole flourish as one pure function of the clock. Every curve in it is here so the shape of
// the beat can be read (and asserted) in one place.
export const waveRevealFrame = (elapsedMs: number): WaveRevealFrame => {
    const elapsed = Math.max(0, elapsedMs)
    if (elapsed >= REVEAL_TOTAL_MS) {
        return {
            done: true,
            alpha: 0,
            scale: 1 - EXIT_SHRINK,
            lift: EXIT_LIFT,
            kickerAlpha: 0,
            epithetAlpha: 0,
            ringT: -1,
            emitting: false,
            panelAlpha: 1,
            panelRise: 0,
        }
    }

    const strike = clamp01(elapsed / REVEAL_STRIKE_MS)
    const handoff = band(elapsed, REVEAL_HOLD_END_MS, REVEAL_TOTAL_MS)
    // The number is gone before the handoff ends, so the panel's last stretch of arriving is
    // uncluttered.
    const leaving = easeOutCubic(handoff / 0.8)
    const entering = easeOutCubic(
        band(elapsed, REVEAL_HOLD_END_MS + REVEAL_HANDOFF_MS * PANEL_ENTRY_AT, REVEAL_TOTAL_MS),
    )

    const settled = SLAM_FROM + (1 - SLAM_FROM) * easeOutBack(strike)
    const breath = handoff > 0 ? 0 : BREATH * Math.sin(elapsed * BREATH_HZ)

    return {
        done: false,
        alpha: easeOutCubic(elapsed / (REVEAL_STRIKE_MS * 0.5)) * (1 - leaving),
        scale: settled + breath - EXIT_SHRINK * easeInOutCubic(handoff),
        lift: EXIT_LIFT * easeInOutCubic(handoff),
        kickerAlpha: band(elapsed, 120, 320) * (1 - leaving),
        epithetAlpha: band(elapsed, 360, 600) * (1 - leaving),
        ringT: strike < 1 ? strike : -1,
        emitting: elapsed > REVEAL_STRIKE_MS * 0.4 && handoff < 0.35,
        panelAlpha: entering,
        panelRise: PANEL_RISE * (1 - entering),
    }
}

// What the flourish says. The big line is the run's one number; the two around it name what that
// number cost and what it was worth, so the player reads a verdict rather than a statistic.
export interface WaveRevealHeadline {
    kicker: string
    headline: string
    epithet: string
}

// Rungs of the ladder, richest-first: the first one the wave clears is the one it gets. The camp's
// wave table runs to 20 (see ember-wave-tempo docs), so the top rung is the whole night survived.
const WAVE_EPITHETS: Array<{ from: number; epithet: string }> = [
    { from: 20, epithet: "THE NIGHT BROKE FIRST" },
    { from: 15, epithet: "DEEP IN THE DARK" },
    { from: 10, epithet: "A LONG VIGIL" },
    { from: 6, epithet: "HARD-WON GROUND" },
    { from: 3, epithet: "THE CAMP HELD" },
    { from: 1, epithet: "THE FIRST OF THEM" },
]

export const waveEpithet = (wave: number): string =>
    (WAVE_EPITHETS.find(rung => wave >= rung.from) || WAVE_EPITHETS[WAVE_EPITHETS.length - 1]).epithet

export const waveRevealHeadline = (summary: { wavesReached?: number }): WaveRevealHeadline => {
    const wave = Math.max(0, Math.floor(summary?.wavesReached || 0))
    // Dying before the first wave lands has no number to report -- "WAVE 0" is not a thing that
    // happened. So this one case turns the big line from a verdict into a shove: the player who
    // sees it got the least out of the camp and is the likeliest to walk away from the game, and
    // three seconds of the screen telling them they achieved nothing is not what should be in
    // front of them while they decide. It names the fact small and puts the way forward big.
    if (wave <= 0) {
        return { kicker: "NO WAVE CAME", headline: "BEGIN AGAIN", epithet: "YOU GOT THIS" }
    }
    return { kicker: "YOU SURVIVED TO", headline: `WAVE ${wave}`, epithet: waveEpithet(wave) }
}
